import { XMLParser, XMLValidator } from "fast-xml-parser";
import { unzipSync, strFromU8 } from "fflate";
import { parseProject, type Project } from "@proof/core";
import {
  fromLegacy,
  toLegacy,
  normalizeLegacy,
  type LegacyEnvelope,
  type LegacyRow,
} from "./index";
import { assertDocumentData } from "./document-data";

const keys = [
  "fields",
  "groups",
  "tabs",
  "triggers",
  "parts",
  "datasets",
  "forms",
  "formInstances",
  "dynamicSources",
  "resourceSnapshots",
] as const;
export type FormConfiguration = Pick<Project, (typeof keys)[number]> & {
  kind: "form-configuration";
  schemaVersion: 1;
  name: string;
  sectionLayout?: "aligned" | "stacked";
  pageflexSourceXml?: string;
};
const PROJECT = "ESM_SOFT_PROOFING_PROJECT";
const VARIABLES = "ESM_SOFT_PROOFING_VARIABLES";
const scopedTables = new Set([
  VARIABLES,
  "ESM_PART",
  "ESM_SOFT_PROOFING_PROJECT_GROUPS",
  "ESM_SOFT_PROOFING_PROJECT_TABS",
  "ESM_SOFT_PROOFING_FORM_INSTANCE",
  "ESM_SOFT_PROOFING_VARIABLE_TRIGGER",
]);
const links = ["PROJECT_ID", "PROOFING_PROJECT_ID", "SOFT_PROOFING_PROJECT_ID"];
const same = (a: unknown, b: unknown) => String(a) === String(b);
const blank = () =>
  fromLegacy({
    tables: { [PROJECT]: [{ PROJECT_ID: 1, PROJECT_NAME: "Untitled form" }] },
  }).projects[0];

/** A transport-free definition: no document, tenant, page, output or project identity. */
export function formConfiguration(project: Project): FormConfiguration {
  return structuredClone({
    kind: "form-configuration",
    schemaVersion: 1,
    name: project.name,
    ...(project.settings.formSectionLayout
      ? { sectionLayout: project.settings.formSectionLayout }
      : {}),
    ...Object.fromEntries(
      keys
        .filter((key) => project[key] !== undefined)
        .map((key) => [key, project[key]]),
    ),
  }) as FormConfiguration;
}
export function parseFormConfiguration(value: unknown): FormConfiguration {
  assertDocumentData(value);
  const raw = value as FormConfiguration;
  if (!raw || raw.kind !== "form-configuration" || raw.schemaVersion !== 1)
    throw new Error("Expected a version 1 form configuration");
  if (
    raw.pageflexSourceXml !== undefined &&
    (typeof raw.pageflexSourceXml !== "string" ||
      raw.pageflexSourceXml.length > 2_000_000)
  )
    throw new Error("Invalid Pageflex source metadata");
  return {
    ...formConfiguration(
      parseProject({
        ...blank(),
        ...Object.fromEntries(keys.map((key) => [key, raw[key]])),
        name: raw.name,
        settings: { formSectionLayout: raw.sectionLayout },
      }),
    ),
    ...(raw.pageflexSourceXml !== undefined
      ? { pageflexSourceXml: raw.pageflexSourceXml }
      : {}),
  };
}

/** Keeps source tables private while exchanging detached configuration snapshots. */
export class FormConfigurationSession {
  private document: Project;
  private original?: LegacyEnvelope;
  private bound = false;
  private projectId: unknown = 1;
  private sourceXml?: string;
  readonly warnings: string[];
  constructor(value?: unknown, options: { projectId?: string | number } = {}) {
    this.warnings = [];
    if (
      value === undefined ||
      (value as FormConfiguration)?.kind === "form-configuration"
    ) {
      this.document = blank();
      if (value !== undefined) this.update(value);
      return;
    }
    assertDocumentData(value);
    const raw = structuredClone(value) as Record<string, unknown>;
    const tables = (raw.tables || raw) as Record<string, LegacyRow[]>;
    const names = Object.keys(tables).filter(
      (k) => k.replace(/^dbo\./i, "").toUpperCase() === PROJECT,
    );
    if (names.length > 1) throw new Error("Duplicate project table aliases");
    const rows = names.length ? tables[names[0]] : [];
    if (!Array.isArray(rows))
      throw new Error("Project table must contain rows");
    this.bound = rows.length > 0;
    if (!this.bound) {
      if (names.length) delete tables[names[0]];
      tables[PROJECT] = [{ PROJECT_ID: 1, PROJECT_NAME: "Untitled form" }];
      for (const [name, records] of Object.entries(tables)) {
        if (!scopedTables.has(name.replace(/^dbo\./i, "").toUpperCase()))
          continue;
        if (!Array.isArray(records)) continue;
        for (const row of records) {
          if (!row || typeof row !== "object") continue;
          for (const key of links)
            if (
              Object.hasOwn(row, key) &&
              row[key] == null &&
              !(key === "PROJECT_ID" && row.PART_ID != null)
            )
              row[key] = 1;
          if (
            name.replace(/^dbo\./i, "").toUpperCase() === VARIABLES &&
            row.PART_ID == null &&
            !row.OVERRIDE_VARIABLE_ID
          )
            row.PROJECT_ID = 1;
        }
      }
    }
    this.original = normalizeLegacy(raw);
    const imported = fromLegacy(this.original);
    const selected =
      !this.bound || options.projectId === undefined
        ? imported.projects.length === 1
          ? imported.projects[0]
          : undefined
        : imported.projects.find((p) =>
            same(p.legacy?.projectRow?.PROJECT_ID, options.projectId),
          );
    if (!selected)
      throw new Error(
        "Select a projectId when loading a dataset containing multiple projects",
      );
    this.document = selected;
    this.sourceXml =
      typeof selected.legacy?.projectRow?.PROJECT_XML === "string"
        ? selected.legacy.projectRow.PROJECT_XML
        : undefined;
    this.projectId = selected.legacy?.projectRow?.PROJECT_ID;
    this.warnings.push(...imported.warnings);
    if (!this.bound && raw._formConfiguration)
      this.update(raw._formConfiguration);
  }
  get value(): FormConfiguration {
    return {
      ...formConfiguration(this.document),
      ...(this.sourceXml !== undefined
        ? { pageflexSourceXml: this.sourceXml }
        : {}),
    };
  }
  /** Internal view model only; hosts store value, not this document scaffold. */
  get editorProject(): Project {
    return structuredClone(this.document);
  }
  update(value: unknown): void {
    const definition = parseFormConfiguration(value);
    this.sourceXml = definition.pageflexSourceXml;
    this.document = parseProject({
      ...this.document,
      ...definition,
      settings: {
        ...this.document.settings,
        formSectionLayout: definition.sectionLayout,
      },
    });
  }
  exportESM(options: { projectId?: number } = {}): LegacyEnvelope {
    const result = toLegacy([this.document], this.original);
    // This editor owns form metadata, never the host's project settings.
    if (this.bound && this.original)
      result.tables[PROJECT] = this.original.tables[PROJECT].map((row) => ({
        ...structuredClone(row),
        extra_json_data:
          result.tables[PROJECT].find((candidate) =>
            same(candidate.PROJECT_ID, row.PROJECT_ID),
          )?.extra_json_data ?? row.extra_json_data,
      }));
    if (this.bound && result._proofStudio && this.original?._proofStudio) {
      result._proofStudio.projects.push(
        ...structuredClone(
          this.original._proofStudio.projects.filter(
            (p) => p.id !== this.document.id,
          ),
        ),
      );
    }
    if (!this.bound) {
      const binding = options.projectId;
      const exportedDefinition = formConfiguration(
        result._proofStudio!.projects[0],
      );
      if (this.sourceXml !== undefined)
        exportedDefinition.pageflexSourceXml = this.sourceXml;
      if (
        binding !== undefined &&
        (!Number.isSafeInteger(binding) || binding < 1)
      )
        throw new Error("projectId must be a positive integer");
      result.tables[PROJECT] = [];
      for (const [name, rows] of Object.entries(result.tables)) {
        if (!scopedTables.has(name)) continue;
        for (const row of rows)
          for (const key of links)
            if (same(row[key], this.projectId)) row[key] = binding ?? null;
      }
      delete result._proofStudio;
      result._formConfiguration = exportedDefinition;
    }
    return structuredClone(result);
  }
}

/** Parse documented variable metadata, never execute Pageflex rules or scripts. */
export function parsePageflexForm(
  xml: string,
  name = "Pageflex form",
): { definition: FormConfiguration; warnings: string[] } {
  if (
    typeof xml !== "string" ||
    new TextEncoder().encode(xml).length > 2_000_000 ||
    (xml.match(/</g)?.length || 0) > 30000
  )
    throw new Error("Pageflex XML exceeds the import limit");
  if (/<!\s*(?:DOCTYPE|ENTITY)/i.test(xml))
    throw new Error("Pageflex XML declarations are not supported");
  if (XMLValidator.validate(xml) !== true)
    throw new Error("Invalid Pageflex XML");
  const parsed = new XMLParser({
    ignoreAttributes: false,
    parseTagValue: false,
    trimValues: false,
    removeNSPrefix: true,
    maxNestedTags: 32,
  }).parse(xml);
  const collection = parsed.project_file?.var_collection;
  if (!collection)
    throw new Error("Expected Pageflex project_file/var_collection metadata");
  const variables = collection.var
    ? Array.isArray(collection.var)
      ? collection.var
      : [collection.var]
    : [];
  if (variables.length > 1000)
    throw new Error("Pageflex project exceeds 1000 variables");
  const warnings: string[] = [],
    seen = new Set<string>(),
    rows: LegacyRow[] = [];
  for (const variable of variables) {
    const key = variable["@_name"],
      kind = variable["@_kind"];
    if (typeof key !== "string" || !key.trim())
      throw new Error("Pageflex variable is missing a name");
    if (seen.has(key)) throw new Error(`Duplicate Pageflex variable: ${key}`);
    seen.add(key);
    if (
      !["Text", "Image", "FormattedPara", "FormattedText"].includes(kind) ||
      variable.var_rule !== undefined ||
      variable.var_script !== undefined
    ) {
      warnings.push(
        `${key}: computed or unsupported variable retained in source XML; no script or rule was executed.`,
      );
      continue;
    }
    const constant = variable.var_const;
    const value =
      typeof constant === "object"
        ? (constant?.["#text"] ?? "")
        : (constant ?? "");
    rows.push({
      VARIABLE_ID: rows.length + 1,
      PROJECT_ID: 1,
      PART_ID: null,
      OVERRIDE_VARIABLE_ID: null,
      PAGEFLEX_VARIABLE_NAME: key,
      DISPLAY_NAME: key,
      CONTROL_TYPE:
        kind === "Image"
          ? "ImageUpload"
          : kind.startsWith("Formatted")
            ? "RichTextArea"
            : "TextBox",
      DEFAULT_VALUE_PARAMETER: String(value),
      IS_USER_VARIABLE: 1,
      IS_EDITABLE: 1,
      DISPLAY_ORDER: rows.length,
      FLAG_IS_IMAGE_VARIABLE: kind === "Image" ? "Y" : "N",
      FLAG_IS_FORMATTED_PARAGRAPH: kind === "FormattedPara" ? "Y" : "N",
    });
  }
  if (!rows.length) throw new Error("No editable Pageflex variables found");
  const project = fromLegacy({
    tables: {
      [PROJECT]: [{ PROJECT_ID: 1, PROJECT_NAME: name, PROJECT_XML: xml }],
      [VARIABLES]: rows,
    },
  }).projects[0];
  const definition = formConfiguration(project);
  // Retain original Pageflex metadata without linking this definition to a document.
  definition.pageflexSourceXml = xml;
  return { definition, warnings };
}

export async function readFormConfigurationUpload(
  file: {
    name: string;
    size: number;
    arrayBuffer(): Promise<ArrayBuffer>;
  },
  options: { projectId?: string | number } = {},
): Promise<{ session: FormConfigurationSession; warnings: string[] }> {
  if (file.size > 10_000_000) throw new Error("Form upload exceeds 10 MB");
  let bytes = new Uint8Array(await file.arrayBuffer()),
    name = file.name;
  if (/\.zip$/i.test(name)) {
    let total = 0,
      count = 0;
    const files = unzipSync(bytes, {
      filter(entry) {
        if (++count > 1000 || (total += entry.originalSize) > 10_000_000)
          throw new Error("Pageflex archive exceeds import limits");
        return (
          /\.(?:pf|xml)$/i.test(entry.name) && !entry.name.includes("__MACOSX/")
        );
      },
    });
    const candidates = Object.entries(files).filter(([, data]) =>
      /<(?:\w+:)?project_file[\s>]/.test(strFromU8(data)),
    );
    if (candidates.length !== 1)
      throw new Error(
        "Upload a ZIP containing exactly one Pageflex project XML/.pf file",
      );
    [name, bytes] = candidates[0];
  }
  const text = strFromU8(bytes).replace(/^\uFEFF/, "");
  if (/^\s*</.test(text)) {
    const parsed = parsePageflexForm(text, name.replace(/\.[^.]+$/, ""));
    return {
      session: new FormConfigurationSession(parsed.definition),
      warnings: parsed.warnings,
    };
  }
  const session = new FormConfigurationSession(JSON.parse(text), options);
  return { session, warnings: session.warnings };
}
